import PageSimpleTemplate from 'modules/app-shell/page-simple-template';
import { FullPageNotification } from 'modules/bots-performance/components/full-page-notification';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  MarketplaceService,
  marketplaceKeys,
} from 'api/services/httpServices/MarketplaceService';
import { screenGte } from 'modules/media/use-media-query';
import useStrategiesState from 'modules/marketplace/hooks/useStrategiesState';
import { StrategiesFilters } from 'modules/marketplace/components/filters';
import mizarIcon from 'assets/img/logo/mizar_icon.svg';
import MarketplaceCardLoading from '../../dex/marketplace/components/marketplace-cards-loading';
import { ErrorPage } from '../../dex/research/components/shared/Common';
import MarketplaceCards from './components/marketplace-cards';
import MainHeader from './components/mainHeader';
import { useUserState } from 'modules/user/UserContext';
import { hasActiveJWT } from 'helpers/api/apiCore';

type Props = {
  initialState?: {
    limit?: number;
  };
};

const Marketplace = ({ initialState }: Props) => {
  const { filtersState } = useStrategiesState();
  const initialLimit =
    initialState?.limit ||
    (window.matchMedia(screenGte.xLarge).matches ? 20 : 10);

  const {
    data,
    isFetching,
    isInitialLoading: isLoading,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryFn: ({ pageParam = { limit: initialLimit, offset: 0 } }) =>
      MarketplaceService.getStrategies({ ...filtersState, ...pageParam }),
    queryKey: marketplaceKeys.getStrategies(filtersState),
    getNextPageParam: (lastPage) => ({
      offset: lastPage.pagination.offset + lastPage.pagination.limit,
      limit: initialLimit,
    }),
    keepPreviousData: true,
  });

  const { user } = useUserState();
  const isLoggedIn = user && 'access_token' in user && hasActiveJWT(user);

  const { data: likes } = useQuery({
    queryKey: marketplaceKeys.getLikes(),
    queryFn: () => MarketplaceService.getLikes(),
    enabled: isLoggedIn,
  });

  const bots = data?.pages.flatMap((page) => page.data) || [];
  const pagination = data?.pages[0].pagination;

  return (
    <PageSimpleTemplate>
      <MainHeader />

      <div className="xxs:mx-1 lg:mx-0 space-y-2 pb-8 md:pb-0">
        <StrategiesFilters />

        {isLoading && (
          <div className="grid grid-cols-1 gap-y-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 sm:gap-x-4">
            {[...Array(initialLimit).keys()].map((channel, id) => {
              return <MarketplaceCardLoading key={id} />;
            })}
          </div>
        )}
        {error && <ErrorPage />}
        {!error && !isLoading && bots?.length === 0 && (
          <FullPageNotification
            height="85vh"
            icon={<img alt="mizar-icon" src={mizarIcon} height="100" />}
            title="No bots found"
            subTitle="It looks like there is nothing matching filters."
            footer={true}
          />
        )}
        {pagination && bots?.length !== 0 && !error && (
          <MarketplaceCards
            isFetching={isFetching}
            pagination={pagination}
            bots={bots}
            likes={likes}
            fetchNextPage={fetchNextPage}
          />
        )}
      </div>
    </PageSimpleTemplate>
  );
};

export default Marketplace;
